// pages/Agents/Agents.js
import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import { hasPermission } from "../../../utils/permissionUtils";
import { Link, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Alert,
  Grid,
  Switch,
  Button,
  CircularProgress,
  Chip,
} from "@mui/material";
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import BoltIcon from '@mui/icons-material/Bolt';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EmailIcon from '@mui/icons-material/Email';
import StorageIcon from '@mui/icons-material/Storage';
import RouterIcon from '@mui/icons-material/Router';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useChatbots } from "../../../contexts/ChatbotContext";

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const colors = {
    textPrimary: '#1e293b',
    textSecondary: '#475569',
    textTertiary: '#94a3b8',
    accent: '#3b82f6',
    success: '#059669',
    warning: '#f59e0b',
    danger: '#dc2626',
};

const AGENT_META = {
    ROUTER: {
        icon: RouterIcon,
        color: '#6366f1',
        description: 'Configures the main routing agent that directs queries to the correct specialist.',
        link: 'router',
    },
    GOOGLE_CALENDAR: {
        icon: CalendarMonthIcon,
        color: '#3b82f6',
        description: 'Allows the AI to manage your Google Calendar events.',
        link: 'google-calendar',
    },
    GMAIL_EMAIL: {
        icon: EmailIcon,
        color: '#dc2626',
        description: 'Allows the AI to manage your Gmail emails - read, search, count, and reply.',
        link: 'gmail-email',
    },
    OUTLOOK_EMAIL: {
        icon: EmailIcon,
        color: '#0078d4',
        description: 'Allows the AI to manage your Outlook emails - read, search, count, and reply.',
        link: 'outlook-email',
    },
    MYSQL: {
        icon: StorageIcon,
        color: '#f59e0b',
        description: 'Allows the AI to query your MySQL database.',
        link: 'mysql',
    },
};

export default function Agents() {
  const [agents, setAgents] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { chatbotId: paramsChatbotId } = useParams();
  const { activeChatbot } = useChatbots();

  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [checkingPermissions, setCheckingPermissions] = useState(true);

  useEffect(() => {
    document.title = "Agent Productivity | Klonit";
    const checkPermissions = async () => {
      setCheckingPermissions(true);
      try {
        const viewPerm = await hasPermission("agents", "view");
        const modifyPerm = await hasPermission("agents", "add|edit");
        const deletePerm = await hasPermission("agents", "delete");
        setCanView(viewPerm);
        setCanModify(modifyPerm);
        setCanDelete(deletePerm);
      } catch (error) {
        console.error("Error checking permissions:", error);
        setCanView(false);
        setCanModify(false);
        setCanDelete(false);
      } finally {
        setCheckingPermissions(false);
      }
    };
    checkPermissions();
  }, []);

  const fetchAgentStatus = useCallback(async () => {
    try {
      const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
      const response = await axiosInstance.get(`/api/agents/status`, { params });
      setAgents(response.data.agents);
    } catch (err) {
      setError("Failed to load agent statuses.");
    } finally {
      setLoading(false);
    }
  }, [paramsChatbotId]);

  useEffect(() => {
    if (canView) fetchAgentStatus();
  }, [fetchAgentStatus, canView]);

  const handleToggle = async (agentName, newStatus) => {
    if (!canModify) return;
    setAgents((prev) => ({
      ...prev,
      [agentName]: { ...prev[agentName], is_active: newStatus },
    }));
    try {
      const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
      await axiosInstance.post(`/api/agents/status`, { agent_name: agentName, is_active: newStatus }, { params });
    } catch (err) {
      setError(`Failed to update ${agentName} status.`);
      setAgents((prev) => ({
        ...prev,
        [agentName]: { ...prev[agentName], is_active: !newStatus },
      }));
    }
  };

  if (checkingPermissions || loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', gap: 2 }}>
        <CircularProgress size={40} sx={{ color: colors.accent }} />
        <Typography sx={{ fontFamily, fontSize: '0.9rem', color: colors.textSecondary }}>
          {checkingPermissions ? 'Checking permissions...' : 'Loading agents...'}
        </Typography>
      </Box>
    );
  }

  if (!canView) {
    return (
      <Box sx={{ fontFamily, maxWidth: '1200px', mx: 'auto' }}>
        <Box sx={{
          bgcolor: 'rgba(255,255,255,0.7)',
          backdropFilter: 'blur(12px)',
          borderRadius: '16px',
          p: 4,
          border: '1px solid rgba(0,0,0,0.06)',
          textAlign: 'center',
        }}>
          <WarningAmberIcon sx={{ fontSize: 48, color: colors.warning, mb: 2 }} />
          <Typography sx={{ fontFamily, fontSize: '1.1rem', fontWeight: 600, color: colors.textPrimary, mb: 1 }}>
            Access Denied
          </Typography>
          <Typography sx={{ fontFamily, fontSize: '0.85rem', color: colors.textSecondary }}>
            You don't have permission to view Agents. Please contact your administrator.
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ fontFamily, maxWidth: '1200px', mx: 'auto' }}>
        <Alert severity="error" sx={{ fontFamily, borderRadius: '12px' }}>{error}</Alert>
      </Box>
    );
  }

  const renderAgentCard = (agentName, config) => {
    const { is_active, is_configured } = config;
    const meta = AGENT_META[agentName] || { icon: PrecisionManufacturingIcon, color: colors.accent, description: 'Unknown agent.', link: '#' };
    const AgentIcon = meta.icon;

    return (
      <Grid item xs={12} md={6} key={agentName}>
        <Box sx={{
          p: 2.5,
          borderRadius: '14px',
          bgcolor: 'rgba(255,255,255,0.8)',
          backdropFilter: 'blur(12px)',
          border: is_active ? `2px solid ${meta.color}33` : '1px solid rgba(0,0,0,0.06)',
          transition: 'all 0.2s ease',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          height: '100%',
          '&:hover': {
            bgcolor: 'rgba(255,255,255,0.95)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
            transform: 'translateY(-2px)',
          },
        }}>
          {/* Top row: icon + name + status */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: 44,
              height: 44,
              borderRadius: '12px',
              bgcolor: `${meta.color}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              <AgentIcon sx={{ fontSize: '1.5rem', color: meta.color }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontFamily, fontSize: '0.95rem', fontWeight: 600, color: colors.textPrimary }}>
                {agentName.replace(/_/g, ' ')} Agent
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
                <Chip
                  icon={is_configured
                    ? <CheckCircleIcon sx={{ fontSize: '0.8rem' }} />
                    : <WarningAmberIcon sx={{ fontSize: '0.8rem' }} />
                  }
                  label={is_configured ? 'Configured' : 'Not Configured'}
                  size="small"
                  sx={{
                    fontFamily,
                    fontSize: '0.65rem',
                    height: '22px',
                    bgcolor: is_configured ? 'rgba(5,150,105,0.1)' : 'rgba(245,158,11,0.1)',
                    color: is_configured ? colors.success : colors.warning,
                    border: 'none',
                    '& .MuiChip-icon': { color: 'inherit' },
                  }}
                />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Switch
                size="small"
                checked={is_active}
                onChange={(e) => handleToggle(agentName, e.target.checked)}
                disabled={(!is_configured && agentName !== "ROUTER") || !canModify}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: meta.color },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: meta.color },
                }}
              />
              <Typography sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, color: is_active ? colors.success : colors.textTertiary }}>
                {is_active ? 'ON' : 'OFF'}
              </Typography>
            </Box>
          </Box>

          {/* Description */}
          <Typography sx={{ fontFamily, fontSize: '0.8rem', color: colors.textSecondary, lineHeight: 1.5 }}>
            {meta.description}
          </Typography>

          {/* Action */}
          {canModify ? (
            <Button
              component={Link}
              to={meta.link}
              variant="text"
              endIcon={<OpenInNewIcon sx={{ fontSize: '0.85rem' }} />}
              sx={{
                fontFamily,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.8rem',
                color: meta.color,
                borderRadius: '10px',
                px: 1.5,
                py: 0.5,
                alignSelf: 'flex-start',
                '&:hover': { bgcolor: `${meta.color}10` },
              }}
            >
              {is_configured ? 'Manage' : 'Configure'}
            </Button>
          ) : (
            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: colors.textTertiary }}>
              Contact administrator to configure
            </Typography>
          )}
        </Box>
      </Grid>
    );
  };

  return (
    <Box sx={{ fontFamily, maxWidth: '1200px', width: '100%', mx: 'auto' }}>
      {/* Header - same as Dashboard */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Box sx={{
          width: 56,
          height: 56,
          borderRadius: '16px',
          background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 2,
          boxShadow: '0 8px 24px rgba(59,130,246,0.3)',
        }}>
          <PrecisionManufacturingIcon sx={{ color: '#fff', fontSize: 28 }} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
          <Typography sx={{
            fontFamily,
            fontSize: '1.5rem',
            fontWeight: 700,
            color: colors.textPrimary,
            letterSpacing: '-0.02em',
          }}>
            Agent Productivity
          </Typography>
          {activeChatbot && (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              bgcolor: 'rgba(59,130,246,0.1)',
              px: 2,
              py: 0.5,
              borderRadius: '20px',
            }}>
              <SmartToyOutlinedIcon sx={{ fontSize: 16, color: colors.accent }} />
              <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: colors.accent }}>
                {activeChatbot.chatbot_name}
              </Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 1 }}>
          <BoltIcon sx={{ fontSize: 14, color: colors.textTertiary }} />
          <Typography sx={{ fontFamily, fontSize: '0.8rem', color: colors.textTertiary }}>
            Connect & manage AI-powered productivity integrations
          </Typography>
        </Box>
      </Box>

      {!canModify && (
        <Box sx={{
          mb: 3,
          p: 2,
          borderRadius: '14px',
          bgcolor: 'rgba(59,130,246,0.06)',
          border: '1px solid rgba(59,130,246,0.15)',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: '10px',
            bgcolor: 'rgba(59,130,246,0.1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <WarningAmberIcon sx={{ color: colors.accent }} />
          </Box>
          <Typography sx={{ fontFamily, fontSize: '0.85rem', color: colors.textSecondary }}>
            You have view-only access. Contact your administrator to make changes.
          </Typography>
        </Box>
      )}


      {/* Built-in Agents Section */}
      <Box sx={{
        bgcolor: 'rgba(255,255,255,0.7)',
        backdropFilter: 'blur(12px)',
        borderRadius: '16px',
        p: 3,
        border: '1px solid rgba(0,0,0,0.06)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: '10px',
            bgcolor: 'rgba(59,130,246,0.1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <PrecisionManufacturingIcon sx={{ fontSize: '1.2rem', color: colors.accent }} />
          </Box>
          <Typography sx={{ fontFamily, fontSize: '1.1rem', fontWeight: 700, color: colors.textPrimary }}>
            Built-in Agents
          </Typography>
        </Box>
        <Typography sx={{ fontFamily, fontSize: '0.8rem', color: colors.textTertiary, mb: 3 }}>
          Connect and configure pre-built integrations for email, calendar, and database access.
        </Typography>
        <Grid container spacing={2}>
          {agents &&
            Object.entries(agents)
              .filter(([name]) => name !== "ROUTER")
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([name, config]) => renderAgentCard(name, config))}
        </Grid>
      </Box>
    </Box>
  );
}
